// Shared story RESOLUTION so the public feed and the owner Primary V1 gallery
// render templates IDENTICALLY: the template's named player is matched to the
// real player record (id + photo) so the circle uses the player's EDITED
// (cropped) headshot, the NAME matches the face, and the CARD's team (crest +
// colour) is aligned to the player's real team — no more faces on random teams.
import { TEAM_INFO, teamColor } from "./teamMap";

export const teamColorOf = (id) => teamColor(id);

export function buildPlayerIndex(players = []) {
    const byName = {};
    const byTeam = {};
    (players || []).forEach((p) => {
        if (!p.photo_url) return;
        if (p.name) byName[p.name.toLowerCase()] = p;
        if (p.team_id) (byTeam[p.team_id] = byTeam[p.team_id] || []).push(p);
    });
    return { byName, byTeam };
}

function hashPick(arr, key) {
    if (!arr || !arr.length) return null;
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
    return arr[h % arr.length];
}

const lastOf = (n) => (n || "").trim().split(/\s+/).slice(-1)[0];
const escapeRe = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const replWord = (text, a, b) => (!text || !a || !b || a === b) ? text : text.replace(new RegExp(escapeRe(a), "g"), b);

function teamParts(id) {
    const full = TEAM_INFO[id]?.name || "";
    const parts = full.split(/\s+/);
    return { full, nick: parts.slice(-1)[0] || "", city: parts.slice(0, -1).join(" "), abbr: TEAM_INFO[id]?.abbr || (id || "").toUpperCase() };
}

// Rewrite headline/body text so the shown NAME (and team references) match the
// player + team actually rendered when a template is re-skinned to a new team.
function swapText(str, oldName, newName, oldTeam, newTeam) {
    let out = str;
    if (oldName && newName && oldName !== newName) {
        out = replWord(out, oldName, newName);
        // Replace every trailing chunk of the old name (handles multi-word names
        // like "Kendall Coyne Schofield" appearing as "Coyne Schofield").
        const ot = oldName.trim().split(/\s+/), nl = lastOf(newName);
        for (let i = 1; i < ot.length; i++) out = replWord(out, ot.slice(i).join(" "), nl);
    }
    if (oldTeam && newTeam && oldTeam !== newTeam) {
        const o = teamParts(oldTeam), n = teamParts(newTeam);
        out = replWord(out, o.full, n.full);
        out = replWord(out, o.nick, n.nick);
        if (o.city) out = replWord(out, o.city, n.city);
        out = replWord(out, o.abbr, n.abbr);
    }
    return out;
}
function applySwap(s, oldName, newName, oldTeam, newTeam) {
    ["line1", "line2", "blurb"].forEach((k) => { if (s[k]) s[k] = swapText(s[k], oldName, newName, oldTeam, newTeam); });
}

// resolveStory(tpl, idx)              → public / ALL: keep the template's named
//                                       player, align card team to that player's real team.
// resolveStory(tpl, idx, targetTeam)  → team sort: RE-SKIN to targetTeam with a real
//                                       player from it AND rewrite the text to match.
export function resolveStory(tpl, idx = { byName: {}, byTeam: {} }, targetTeam = null, picked = null, ctx = null) {
    const s = { ...tpl.story };
    // Manual player pick (Creator search) wins — swap identity + text to match.
    if (picked && s.subject && s.subject.kind === "player") {
        const oldName = s.subject.name, oldTeam = s.subject.team_id || s.teamId;
        const t = picked.team_id || oldTeam;
        s.subject = { ...s.subject, id: picked.id, name: picked.name, photo_url: picked.photo_url, team_id: t };
        s.teamId = t; s.teamColor = teamColorOf(t);
        applySwap(s, oldName, picked.name, oldTeam, t);
        return s;
    }
    if (s.subject && s.subject.kind === "player") {
        if (targetTeam) {
            const p = hashPick(idx.byTeam[targetTeam], tpl.key);
            const oldName = s.subject.name;
            const oldTeam = s.subject.team_id || s.teamId;
            s.teamId = targetTeam; s.teamColor = teamColorOf(targetTeam);
            if (s.oppId === targetTeam) s.oppId = null;
            if (p) {
                s.subject = { ...s.subject, id: p.id, photo_url: p.photo_url, name: p.name, team_id: targetTeam };
                applySwap(s, oldName, p.name, oldTeam, targetTeam);
            } else {
                s.subject = { ...s.subject, team_id: targetTeam, photo_url: null };
                applySwap(s, null, null, oldTeam, targetTeam);
            }
        } else {
            const named = idx.byName[(s.subject.name || "").toLowerCase()];
            if (named && named.photo_url) {
                const rt = named.team_id || s.teamId;
                s.subject = { ...s.subject, id: named.id, photo_url: named.photo_url, name: named.name, team_id: rt };
                if (named.team_id) { s.teamId = named.team_id; s.teamColor = teamColorOf(named.team_id); }
            }
        }
    } else if (s.subject && s.subject.kind === "team") {
        const oldTeam = s.subject.team_id || s.teamId;
        const t = targetTeam || oldTeam;
        if (targetTeam) {
            s.teamId = targetTeam; s.teamColor = teamColorOf(targetTeam);
            if (s.oppId === targetTeam) s.oppId = null;
            applySwap(s, null, null, oldTeam, targetTeam);
        }
        s.subject = { ...s.subject, team_id: t, name: TEAM_INFO[t]?.name || s.subject.name };
    } else if (targetTeam && s.teamId) {
        const oldTeam = s.teamId;
        s.teamId = targetTeam; s.teamColor = teamColorOf(targetTeam);
        applySwap(s, null, null, oldTeam, targetTeam);
    }
    if (!s.teamColor && s.teamId) s.teamColor = teamColorOf(s.teamId);
    // Resolve any nested player subjects (three-stars, lineups) by name so their
    // real cropped headshot + team show, same as the main subject.
    const resolveEntry = (entry) => {
        const sub = entry && entry.subject;
        if (!sub || sub.kind !== "player") return entry;
        const named = idx.byName[(sub.name || "").toLowerCase()];
        if (named && named.photo_url) return { ...entry, subject: { ...sub, id: named.id, photo_url: named.photo_url, name: named.name, team_id: named.team_id || sub.team_id } };
        return entry;
    };
    if (Array.isArray(s.stars)) s.stars = s.stars.map(resolveEntry);
    if (Array.isArray(s.lineup)) s.lineup = s.lineup.map(resolveEntry);
    // Resolve lineup unit players (Lines / Pairs) by name → real cropped headshot.
    if (Array.isArray(s.units)) s.units = s.units.map((u) => ({ ...u, players: (u.players || []).map((p) => {
        const named = idx.byName[(p.name || "").toLowerCase()];
        return named ? { ...p, id: named.id, photo_url: p.photo_url || named.photo_url, team_id: named.team_id || p.team_id } : p;
    }) }));
    // Team-sort real context: Team-vs-Team uses the sorted team's actual opponent
    // (most recent / upcoming game) and Three Stars uses that team's own players.
    if (targetTeam && ctx) {
        if (tpl.layout === "game_vs" && ctx.oppId) s.oppId = ctx.oppId;
        if (tpl.layout === "game_stars3" && Array.isArray(ctx.stars) && ctx.stars.length && Array.isArray(s.stars)) {
            s.stars = s.stars.map((st, i) => {
                const c = ctx.stars[i];
                return c ? { ...st, subject: { ...(st.subject || {}), kind: "player", id: c.id, name: c.name, photo_url: c.photo_url, team_id: targetTeam } } : st;
            });
            if (ctx.matchup) s.matchup = ctx.matchup;
        }
    }
    return s;
}

export function storyMatchesTeam(s, team) {
    if (!team) return true;
    return s.teamId === team || s.oppId === team || s.subject?.team_id === team;
}

// ---- PUBLIC ← OWNER Creator overrides ---------------------------------------
// The public timeline is built from real data with per-event template keys, so
// it can't match the owner's saved overrides directly. We map each real item to
// the PRIMARY V1 template it renders as, then apply the owner's saved edits
// (hidden elements, text position, logo/crest style, text colour, hide-whole-
// template) — so what the owner sets on the Templates page shows on public.
const FEED_TYPE_KEY = {
    trade: "feed_trade", signing: "feed_signing", extension: "feed_signing", movement: "feed_trade",
    news: "feed_news", recap: "feed_recap", matchup: "feed_matchup", three_stars: "feed_three_stars",
    milestone_pre: "feed_milestone_watch", milestone_live: "feed_milestone_live",
};
const GAME_LAYOUT_KEY = {
    game_today: "game_today", game_today_preview: "game_today_preview", game_today_recap: "game_today_recap",
    game_stars3: "game_stars3", game_draft: "game_draft", game_goalie: "game_goalie",
    game_milestone: "game_milestone", game_record: "game_record", game_gd: "game_gd",
    game_milestone_prestige: "game_milestone_prestige", game_record_prestige: "game_record_prestige",
    v1_news: "v1_news", v1_signing: "v1_signing", v1_resign: "v1_resign", v1_trade: "v1_trade",
    v3_record_breaker: "v3_record_breaker",
    v3_milestone: "v3_milestone",
    v1_milestone_marquee: "v1_milestone_marquee", v1_milestone_number: "v1_milestone_number",
    v1_milestone_ticket: "v1_milestone_ticket", v1_milestone_timeline: "v1_milestone_timeline",
    v1_milestone_medallion: "v1_milestone_medallion",
    game_vs_series: "game_vs_series",
    game_trade: "game_trade", game_flashback: "game_flashback",
};

export function v1KeyForItem(it) {
    // An explicit per-template id wins — this is the SAME id the owner edits in
    // the gallery, so a public card maps 1:1 to its Creator-edited template.
    if (it.tpl?.v1Key) return it.tpl.v1Key;
    const layout = it.tpl?.layout;
    if (layout === "slides") return "slides_recap";
    if (layout === "game_vs") return it.s?.score ? "game_vs_result" : "game_vs_pre";
    if (GAME_LAYOUT_KEY[layout]) return GAME_LAYOUT_KEY[layout];
    if (layout === "feedcard" || it.tpl?.feedCard) return FEED_TYPE_KEY[it.type] || FEED_TYPE_KEY[it.s?.type] || null;
    return null;
}

// Publish/edit KEY for an item = its DESIGN template (v1Key). Contract cards map
// to one of two templates: feed_contract_signed / feed_contract_extended.
const CONTRACT_KEYS = new Set(["feed_contract_signed", "feed_contract_extended", "feed_contract_resigned"]);
export function pubKeyForItem(it) {
    return v1KeyForItem(it);
}

// Templates that PUBLISH AUTOMATICALLY (visible by default, opt-OUT via `deleted`)
// in the 4:5 (v) format. Everything else is opt-IN via `published`. This is the
// SINGLE source of truth used by BOTH the public resolver and the gallery toggle
// so a gallery switch always matches the public view.
// The ONLY templates that are LIVE by default (in the 4:5 `v` format). Every
// other template is OFF by default. This is the owner's approved live set:
// contracts, today's games (preview/recap), next game, standings (per-game +
// day-recap) + magic #, three stars, starting goalie, birthday, team-vs
// (preview/recap) and GD 6/8. BOTH standings templates are live by default so
// they stay selectable in the Live view and can be grouped/merged freely; the
// PER-GAME one is normally dropped into the Play-by-Play group (its S/GC ticks
// then govern visibility), the DAY-RECAP one is the standalone once-per-day card.
const DEFAULT_ON_KEYS = new Set([
    "feed_contract_signed", "feed_contract_extended", "feed_contract_resigned",
    "feed_camp_invite",
    "game_today_preview", "game_today_recap", "game_next",
    "game_standings", "game_standings_day", "game_stars3", "game_goalie_duo",
    "misc_birthday", "game_vs_pre", "game_vs_result", "game_gd",
]);
export function isDefaultOn(key, fmt) {
    return fmt === "v" && DEFAULT_ON_KEYS.has(key);
}

// Publishing is per (key | format). Contract templates in the 4:5 (v) format are
// VISIBLE by default (opt-out via `deleted`); every other (key|format) is HIDDEN
// by default (opt-in via `published`). 4:5 and 16:9 are fully independent.
export function isPublished(studio, key, fmt) {
    if (!key) return false;
    const full = `${key}|${fmt}`;
    if (isDefaultOn(key, fmt)) return !(((studio && studio.deleted) || []).includes(full));
    return (((studio && studio.published) || []).includes(full));
}

// True when this card is NOT published for its format (so it stays off public).
export function isItemHidden(it, studio) {
    // A slide can force itself onto the public Stories feed (e.g. a game-winning
    // shootout goal) regardless of its template's Stories surface toggle.
    if (it && it.s && it.s._forceStory) return false;
    const k = pubKeyForItem(it);
    const groups = (studio && studio.groups) || [];
    const grouped = k && groups.some((g) => (g.keys || []).includes(k));
    // Grouped members AND PBP templates each carry independent Stories (S) and
    // Game Centre (GC) surface toggles. On the public Stories feed a slide shows
    // only when its S surface is on (default on for most, off for shot/faceoff/
    // goalie). Ungrouped non-PBP templates use the normal publish flag.
    if (grouped || PBP_SURFACE_KEYS.includes(k)) return !pbpSurface(studio, k).s;
    return !isPublished(studio, k, it.orientation || "v");
}

// Game-Centre variant: identical to isItemHidden but gated by the GC surface, so
// a slide (PBP event OR result/three-stars/next-game, etc.) is part of the Game
// Centre recap only when its GC surface is on.
export function isItemHiddenGC(it, studio) {
    const k = pubKeyForItem(it);
    const groups = (studio && studio.groups) || [];
    const grouped = k && groups.some((g) => (g.keys || []).includes(k));
    if (grouped || PBP_SURFACE_KEYS.includes(k)) return !pbpSurface(studio, k).gc;
    return !isPublished(studio, k, it.orientation || "v");
}

// ---- PBP surfaces: each PBP template shows on the public Stories timeline (S)
// and/or the per-game Game Centre (GC), toggled independently. Untick both →
// it appears NOWHERE. Defaults: goal + penalty show on both; shot/faceoff/goalie
// are Game-Centre-only.
export const PBP_SURFACE_KEYS = ["pbp_goal_spot", "pbp_shootout_spot", "pbp_goal_ticker", "pbp_penalty_ticker", "pbp_shot_ticker", "pbp_faceoff_ticker", "pbp_goalie_ticker", "pbp_hit_ticker", "pbp_block_ticker", "pbp_shootout_ticker"];
const PBP_SURFACE_DEFAULTS = {
    // Game Recap spotlights (Stories feed only — NOT the Game Centre timeline).
    pbp_goal_spot: { s: true, gc: false },
    pbp_shootout_spot: { s: true, gc: false },
    // Play-by-Play tickers. The meaningful events default ON in the feed's
    // Play-by-Play group; shots / faceoffs / goalie changes stay Game-Centre-only
    // by default (too noisy for the feed) but can be toggled on.
    pbp_goal_ticker: { s: true, gc: true },
    pbp_penalty_ticker: { s: true, gc: true },
    pbp_hit_ticker: { s: true, gc: true },
    pbp_block_ticker: { s: true, gc: true },
    pbp_shootout_ticker: { s: true, gc: true },
    pbp_goalie_ticker: { s: false, gc: true },
    pbp_shot_ticker: { s: false, gc: true },
    pbp_faceoff_ticker: { s: false, gc: true },
};
export function pbpSurface(studio, key) {
    const d = PBP_SURFACE_DEFAULTS[key] || { s: true, gc: true };
    const o = ((studio && studio.pbpSurfaces) || {})[key] || {};
    return { s: o.s == null ? d.s : !!o.s, gc: o.gc == null ? d.gc : !!o.gc };
}

// The saved config for ONE entity = (template + format [+ skin]). STRICT per
// format — a 16:9 card NEVER inherits 4:5 edits — but SHARED across teams. A
// team-style skin (e.g. bos_v2) is its OWN entity: edits are keyed with the
// skin suffix so V1 and V2 never bleed into each other. Corners are a per-format
// global under `__fmt__|{orient}`.
export function overrideFor(studio, vkey, orient, skin = null) {
    const square = studio?.overrides?.[`__fmt__|${orient}`]?.square;
    if (!studio || !studio.overrides || !vkey) return { rest: {}, hideEls: [], square };
    const ovs = studio.overrides;
    const base = skin ? `${vkey}|${orient}|${skin}` : `${vkey}|${orient}`;
    const ov = ovs[base] || (!skin && (ovs[`${vkey}|${orient}|all`])) || null;   // |all = legacy
    if (!ov) return { rest: {}, hideEls: [], square };
    const { _hide, _playerId, ...rest } = ov;   // real players stay real → skip _playerId
    return { rest, hideEls: _hide || [], square };
}

// Which team-style skin applies to this PUBLIC item, if any. Each team has its
// OWN bespoke contract V2 skin, gated by the owner's per-team "Live" switch.
const CONTRACT_SET = new Set(["feed_contract_signed", "feed_contract_extended", "feed_contract_resigned"]);
export function skinForItem(it, studio) {
    if (!studio || !studio.teamStyles) return null;
    const key = pubKeyForItem(it);
    const team = it.s?.teamId;
    if (CONTRACT_SET.has(key) && team && studio.teamStyles[`${team}_v2`]?.live) return `${team}_v2`;
    return null;
}

// Return the item with the owner's saved overrides applied (or the item as-is).
export function applyStudioToItem(it, studio) {
    const key = pubKeyForItem(it);
    const skin = skinForItem(it, studio);
    const { rest, hideEls, square } = overrideFor(studio, key, it.orientation || "v", skin);
    return { ...it, skin, s: { ...it.s, ...rest }, hideEls, square };
}

// ---- CREATOR grouping + priority (public timeline) --------------------------
// A GROUP fuses two-or-more DIFFERENT templates into ONE slide deck wherever
// they co-occur on the SAME day (solo if only one is present). A same-key
// "slide" mode (multiMode) fuses MULTIPLE cards of the SAME template (e.g. two
// birthdays) into one deck. PRIORITY orders which card/deck comes first within
// a day. Config lives in the shared studio doc so it's global + permanent.
function makeSlideItem(id, its, groupId) {
    const first = its[0];
    return {
        id: `grp-${id}-${first.date}`,
        _groupId: groupId,
        date: first.date,
        season: first.season,
        eventId: `grp-${id}-${first.date}`,
        orientation: "v",
        sourceRef: first.sourceRef || null,
        _isGame: its.some((i) => i._isGame),
        tpl: { key: `grp_${id}`, layout: "slides" },
        s: {
            _groupId: groupId,
            teamColor: first.s?.teamColor, teamId: first.s?.teamId,
            slides: its.map((i) => ({ item: { tpl: i.tpl, s: i.s, hideEls: i.hideEls || [], square: i.square, skin: i.skin } })),
        },
    };
}

// A GROUP now renders its members SIDE BY SIDE (a horizontal row) in the live
// template order — NOT shrunk into a slide deck. Optional group name = a header.
function makeRowItem(id, its, groupId, name = "") {
    const first = its[0];
    return {
        id: `grp-${id}-${first.date}`,
        _groupId: groupId,
        date: first.date,
        season: first.season,
        eventId: `grp-${id}-${first.date}`,
        orientation: "v",
        _isGame: its.some((i) => i._isGame),
        tpl: { key: `grp_${id}`, layout: "group_row" },
        s: {
            groupName: name || "",
            teamColor: first.s?.teamColor, teamId: first.s?.teamId,
            items: its.map((i) => ({ tpl: i.tpl, s: i.s, hideEls: i.hideEls || [], square: i.square, skin: i.skin })),
        },
    };
}

// items = the day's items for one orientation. Returns the day's items after
// grouping + same-key slide fusion + priority ordering.
export function applyGroupsAndPriority(items, studio) {
    const groups = (studio && studio.groups) || [];
    const priority = (studio && studio.priority) || [];
    const multiMode = (studio && studio.multiMode) || {};
    const keyToGroup = {};
    groups.forEach((g) => (g.keys || []).forEach((k) => { keyToGroup[k] = g.id; }));

    // Bucket by group (grouped templates) vs pubKey (ungrouped). GROUP = SLIDE:
    // ANY set of grouped templates that lands on the same day fuses into a slide
    // deck. If EVERY member of a group carries a game id (`_gameId`), we split the
    // group into one deck PER GAME (so each game is its own deck — PBP, standings,
    // etc). Otherwise (mixed or day-level members) the whole group fuses into a
    // SINGLE day deck, so anything you merge in always shows up in the deck.
    const groupItems = {};    // gid -> items[]
    const looseBuckets = {};  // key -> { key, items }
    items.forEach((it) => {
        const k = pubKeyForItem(it);
        const gid = keyToGroup[k];
        if (gid) (groupItems[gid] = groupItems[gid] || []).push(it);
        else (looseBuckets[k] = looseBuckets[k] || { key: k, items: [] }).items.push(it);
    });

    const out = [];
    // The trailing day-summary cards (Next Game, Standings) are the ONLY non-PBP
    // slides a play-by-play card must jump ahead of. Everything else keeps its
    // AUTOMATIC order (group key order / drag-and-drop), and PBP↔PBP stays
    // chronological. So SOGW/shootout slots in front of Next Game + Standings
    // WITHOUT disturbing any leading cards (recap/result) or the PBP sequence.
    // Deck ordering is fixed by PHASE regardless of drag order so play-by-play
    // never leaks past the day-summary cards: Team-vs-Team RESULT → Three Stars
    // → ALL play-by-play (any order among themselves, chronological by _pbpSeq)
    // → Next Game → Standings. Anything else sits between Three Stars and PBP.
    const phaseOf = (it) => {
        const k = pubKeyForItem(it);
        if (k === "game_goalie_duo" || k === "game_goalie") return -1;
        if (k === "game_vs_result" || k === "game_vs_playoff" || k === "game_vs_preseason") return 0;
        if (k === "game_stars3" || k === "feed_three_stars") return 1;
        if (it.s?._pbpSeq != null || PBP_SURFACE_KEYS.includes(k)) return 3;
        if (k === "game_next" || k === "game_next_slate") return 4;
        if (k === "game_standings" || k === "game_standings_day") return 5;
        return 2;
    };
    // The owner's MANUAL group order (drag/arrows, stored in group.keys) is now
    // authoritative on the public feed too — so what they arrange is what shows.
    // _pbpSeq only breaks ties between cards of the SAME template (chronological
    // play-by-play). Unlisted keys fall to the end.
    const orderSort = (arr, order) => [...arr].sort((a, c) => {
        const ia = order.indexOf(pubKeyForItem(a)); const ic = order.indexOf(pubKeyForItem(c));
        const ra = ia === -1 ? 9999 : ia; const rc = ic === -1 ? 9999 : ic;
        if (ra !== rc) return ra - rc;
        const sa = a.s?._pbpSeq, sc = c.s?._pbpSeq;
        if (sa != null && sc != null) return sa - sc;
        return 0;
    });
    Object.keys(groupItems).forEach((gid) => {
        const g = groups.find((x) => x.id === gid);
        const order = (g && g.keys) || [];
        // A group flagged `row:true` renders its members SIDE BY SIDE in a
        // horizontal row (standalone-looking cards), NOT fused into a slide deck.
        const isRow = !!(g && g.row);
        const groupName = (g && g.title) || "";
        const its = groupItems[gid];
        // How many DISTINCT games are represented in this group on this day?
        const gameIds = [...new Set(its.filter((i) => i.s && i.s._gameId).map((i) => i.s._gameId))];
        const decks = {};
        if (gameIds.length <= 1) {
            // Single game (or day-level only): the whole group fuses into ONE
            // deck — even members without a _gameId still join it.
            decks["_all"] = its;
        } else {
            // MULTIPLE games on the same day → one deck PER GAME so Game 1 and
            // Game 2 never merge. Members without a _gameId (day-level, e.g. the
            // end-of-day next-games slate) form their OWN separate deck.
            its.forEach((i) => {
                const dk = (i.s && i.s._gameId) ? i.s._gameId : "_day";
                (decks[dk] = decks[dk] || []).push(i);
            });
        }
        Object.keys(decks).forEach((dk) => {
            const sorted = orderSort(decks[dk], order);
            const suffix = dk !== "_all" ? `-${dk}` : "";
            if (sorted.length >= 2) out.push(isRow ? makeRowItem(gid + suffix, sorted, gid, groupName) : makeSlideItem(gid + suffix, sorted, gid));
            else out.push(...sorted);   // a lone grouped member renders on its own
        });
    });
    Object.values(looseBuckets).forEach((b) => {
        if (multiMode[b.key] === "slide" && b.items.length >= 2) out.push(makeSlideItem(b.key, b.items, b.key));
        else out.push(...b.items);
    });

    const rank = (it) => {
        const key = it._groupId || pubKeyForItem(it);
        let i = priority.indexOf(key);
        // A lone member of a group (only one of the group's cards landed on this
        // day, so it was emitted standalone rather than fused) inherits its GROUP's
        // priority — otherwise its raw key (often absent from `priority`) sinks to
        // the very end (e.g. Starting In Net / upcoming showing last).
        if (i === -1 && !it._groupId) {
            const gid = keyToGroup[pubKeyForItem(it)];
            if (gid) i = priority.indexOf(gid);
        }
        return i === -1 ? 9999 : i;
    };
    return out.map((it, i) => ({ it, i })).sort((a, b) => (rank(a.it) - rank(b.it)) || (a.i - b.i)).map((x) => x.it);
}
